<template>
  <!--
    The trialStart check is not sufficient as a check for whether or not to show/hide countdown,
    rather as a fallback in the unexpected case there is no trialStart so we don't show NaN
    to the user.
    Use FeatureTrialModule.getPremiumBadgeProps.showFeatureUsage to know whether to show/hide this
  -->
  <PremiumFeatureBadge
    v-if="trialStart"
    :data-test-id="$testId('premiumBadge')"
    :label="isFairUseFlagOn ? 'TRIAL' : ''"
  >
    <div
      class="badgeContent"
    >
      <DaysInTrialCounter
        v-if="isFairUseFlagOn"
        hasPeriod
        :trialStart="trialStart"
      />
      <span
        v-else
        class="flagOffContent"
        :data-test-id="$testId('flagOffContent')"
      >
        {{ preFeatureFlagContent }}
      </span>
      <span v-if="showLearnMore || isFairUseFlagOn">
        <ButtonLink
          class="learnMoreButton"
          :data-test-id="$testId('flagOnLearnMoreButton')"
          text="Learn More"
          @click="handleClick"
        />.
      </span>
    </div>
  </PremiumFeatureBadge>
</template>

<script>
import {
  ButtonLink,
} from '@jumpcloud/ui-components';
import { mapGetters } from 'vuex';
import { trialPropsData } from 'client/util/Constants/FeatureTrialConstants';
import DaysInTrialCounter from 'client/components/Trials/DaysInTrialCounter.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PremiumFeatureBadge from 'client/components/Badges/PremiumFeatureBadge.vue';
import moment from 'moment';

const featureTrialModule = 'FeatureTrialModule';

export default {
  name: 'FairUseCountdownBadge',

  components: {
    PremiumFeatureBadge,
    ButtonLink,
    DaysInTrialCounter,
  },

  props: {
    featureCode: {
      required: true,
      type: String,
    },
    includeFeatureName: {
      type: Boolean,
      default: false,
    },
    showLearnMore: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    ...mapGetters(featureTrialModule, [
      'getFeatureUsage',
    ]),

    isFairUseFlagOn() {
      return this.$hasFeatureFlag(this.$featureFlags.fairUseChanges);
    },

    modalProps() {
      return trialPropsData[this.featureCode] ?? {};
    },

    preFeatureFlagContent() {
      const currentDate = moment();
      const timeSinceDays = currentDate.diff(moment(this.trialStart), 'days') + 1; // +1 because we don't want to show 0 days on day 1
      const daysText = timeSinceDays > 1 ? 'days' : 'day';
      const { featureName } = this.modalProps;
      const baseText = this.includeFeatureName
        ? `You have been trying ${featureName} for`
        : 'You have been using this feature for';

      return `${baseText} ${timeSinceDays} ${daysText}.`;
    },

    trialStart() {
      return this.getFeatureUsage(this.featureCode)?.startDate;
    },
  },

  methods: {
    handleClick() {
      ModalEventBus.open({
        component: 'TrialModal',
        componentProps: { ...this.modalProps, showWelcomeMessage: false },
        isLargeModal: true,
      });

      this.$emit('click');
    },
  },
};
</script>

<style scoped>
.badgeContent {
  align-items: center;
  display: flex;
  gap: var(--jc-spacer-small);
}

.flagOffContent {
  font-weight: 500;
}

.learnMoreButton {
  margin-top: 1px;
}
</style>
